import React, { useState, useEffect, useCallback } from 'react';
import { sharepointAPI } from '../utils/api';
import SharePointFolderPicker from './SharePointFolderPicker';

function SharePointCopyModal({ pack, onClose, onCopied }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connection, setConnection] = useState(null);
  const [link, setLink] = useState(null);
  const [files, setFiles] = useState([]);
  const [checked, setChecked] = useState({}); // key → bool
  const [dreDest, setDreDest] = useState(null); // { id, path }
  const [showStationPicker, setShowStationPicker] = useState(false);
  const [showDrePicker, setShowDrePicker] = useState(false);
  const [copying, setCopying] = useState(false);
  const [results, setResults] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await sharepointAPI.getPackSharePoint(pack.id);
      setConnection(res.data.connection);
      setLink(res.data.link);
      setFiles(res.data.files || []);
      const init = {};
      (res.data.files || []).forEach((f) => { init[f.key] = !!f.preTick; });
      setChecked(init);
      if (res.data.link?.dre_dest_item_id) setDreDest({ id: res.data.link.dre_dest_item_id, path: res.data.link.dre_dest_path });
      else if (res.data.connection?.dreFolderItemId) setDreDest({ id: res.data.connection.dreFolderItemId, path: res.data.connection.dreFolderPath });
    } catch (err) { setError(err.response?.data?.error || 'Failed to load'); }
    finally { setLoading(false); }
  }, [pack.id]);
  useEffect(() => { load(); }, [load]);

  const stationFiles = files.filter((f) => f.group === 'station');
  const dreFiles = files.filter((f) => f.group === 'dre');
  // Toggling the selection clears a prior result so the success banner doesn't linger stale
  const toggle = (key) => { setResults(null); setChecked((c) => ({ ...c, [key]: !c[key] })); };
  const selectedKeys = Object.keys(checked).filter((k) => checked[k]);
  const dreSelected = dreFiles.some((f) => checked[f.key]);

  const saveStationFolder = async (folder) => {
    setShowStationPicker(false);
    try {
      const res = await sharepointAPI.setPackFolder(pack.id, { itemId: folder.id, path: folder.path, webUrl: folder.webUrl });
      setLink(res.data.link);
    } catch (err) { setError(err.response?.data?.error || 'Failed to link folder'); }
  };

  const runCopy = async () => {
    setCopying(true); setError(''); setResults(null);
    try {
      const res = await sharepointAPI.copy(pack.id, selectedKeys, dreSelected && dreDest ? { itemId: dreDest.id, path: dreDest.path } : undefined);
      setResults(res.data.results || []);
      if (onCopied) onCopied();
    } catch (err) { setError(err.response?.data?.error || 'Copy failed'); }
    finally { setCopying(false); }
  };

  const notConnected = !connection || !connection.connected;
  const needStationFolder = stationFiles.some((f) => checked[f.key]) && !link?.folder_item_id;

  const copiedCount = results ? results.filter((r) => r.status === 'copied').length : 0;
  const failedCount = results ? results.length - copiedCount : 0;
  const allCopied = !!results && copiedCount > 0 && failedCount === 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => !copying && onClose()}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Copy to SharePoint {'—'} Station {pack.station_no}</h3>
        {pack.derivedStatus !== 'granted' && (
          <p className="text-xs text-amber-600 mb-2">This pack isn&apos;t granted yet {'—'} you can still copy, but documents may not be final.</p>
        )}

        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading&hellip;</div>
        ) : notConnected ? (
          <div className="text-sm text-red-600 py-6">SharePoint isn&apos;t connected yet. Ask a System Administrator to set it up in the Setup tab.</div>
        ) : (
          <>
            {/* Station folder link */}
            <div className="rounded-lg border border-gray-200 p-3 mb-3 text-sm flex items-center justify-between gap-2">
              <div className="min-w-0">
                <span className="text-gray-500">Station folder: </span>
                {link?.folder_path ? <span className="font-medium text-gray-800">{link.folder_path}</span> : <span className="text-amber-600">not linked</span>}
              </div>
              <button onClick={() => setShowStationPicker(true)} className="text-xs font-medium text-primary-600 hover:text-primary-800 flex-shrink-0">{link?.folder_item_id ? 'Change' : 'Link folder'}</button>
            </div>

            {/* Group A — station documents */}
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Station documents</h4>
            <div className="divide-y divide-gray-100 mb-4">
              {stationFiles.length === 0 && <p className="text-sm text-gray-400 py-2">No station documents available.</p>}
              {stationFiles.map((f) => (
                <label key={f.key} className="flex items-center gap-3 py-2 cursor-pointer">
                  <input type="checkbox" checked={!!checked[f.key]} onChange={() => toggle(f.key)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                  <span className="flex-1 min-w-0">
                    <span className="text-sm text-gray-800 capitalize">{f.docLabel}</span>
                    <span className="text-xs text-gray-400 ml-2">{f.filename} &middot; {f.revisionLabel}</span>
                  </span>
                  <span className="text-xs text-gray-500">{'→'} {f.targetTypeKey.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>

            {/* Group B — DRE letter */}
            {dreFiles.length > 0 && (
              <>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">DRE / Site Handover letter</h4>
                <div className="mb-4">
                  {dreFiles.map((f) => (
                    <div key={f.key} className="flex items-center gap-3 py-2">
                      <input type="checkbox" checked={!!checked[f.key]} onChange={() => toggle(f.key)} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                      <span className="flex-1 min-w-0 text-sm text-gray-800">{f.docLabel} <span className="text-xs text-gray-400 ml-1">{f.filename}</span></span>
                      <span className="text-xs text-gray-500 truncate max-w-[220px]">{'→'} {dreDest?.path || 'DRE folder'}</span>
                      <button onClick={() => setShowDrePicker(true)} className="text-xs font-medium text-primary-600 hover:text-primary-800">Change&hellip;</button>
                    </div>
                  ))}
                </div>
              </>
            )}

            {results && (
              <div className="mb-3">
                <div className={`rounded-lg border p-3 mb-2 text-sm font-medium flex items-center gap-2 ${allCopied ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                  <span className="text-base leading-none">{allCopied ? '✓' : '⚠'}</span>
                  <span>
                    {allCopied
                      ? `Done — ${copiedCount} file${copiedCount === 1 ? '' : 's'} copied to SharePoint. You can close this window.`
                      : `${copiedCount} of ${results.length} file${results.length === 1 ? '' : 's'} copied${failedCount ? ` · ${failedCount} failed — see below and try again` : ''}.`}
                  </span>
                </div>
                <div className="rounded-lg border border-gray-200 p-3 text-sm">
                  {results.map((r) => (
                    <div key={r.key} className="flex items-center gap-2 py-0.5">
                      <span className={r.status === 'copied' ? 'text-emerald-600' : 'text-red-600'}>{r.status === 'copied' ? '✓' : '✗'}</span>
                      <span className="text-gray-700">{r.filename}</span>
                      {r.error && <span className="text-xs text-red-500">{'—'} {r.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && <p className="text-xs text-red-600 mb-2">{error}</p>}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} disabled={copying}
                className={`px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${allCopied ? 'bg-primary-600 text-white hover:bg-primary-700' : 'text-gray-600 border border-gray-300 hover:bg-gray-50'}`}>
                {allCopied ? 'Done' : 'Close'}
              </button>
              <button onClick={runCopy} disabled={copying || selectedKeys.length === 0 || needStationFolder}
                className={`px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${allCopied ? 'text-primary-600 border border-primary-200 hover:bg-primary-50' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
                title={needStationFolder ? 'Link a station folder first' : ''}>
                {copying ? 'Copying…' : results ? 'Copy again' : `Copy ${selectedKeys.length} file${selectedKeys.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        )}
      </div>

      {showStationPicker && (
        <SharePointFolderPicker title="Select the station folder" onSelect={saveStationFolder} onClose={() => setShowStationPicker(false)} />
      )}
      {showDrePicker && (
        <SharePointFolderPicker
          title="Choose where the DRE letter goes"
          rootItemId={connection?.dreFolderItemId || null}
          rootLabel="DRE folder"
          onSelect={(folder) => { setDreDest({ id: folder.id, path: folder.path }); setShowDrePicker(false); }}
          onClose={() => setShowDrePicker(false)}
        />
      )}
    </div>
  );
}

export default SharePointCopyModal;
